import { Star } from "lucide-react";
import { REVIEW_SUMMARY } from "@/data/reviews";

/**
 * Wiederverwendbares Google-Rating-Badge: Sterne + Score + Bewertungs-Anzahl.
 * Wird in Header (kompakt), Startseite-Sektion (groß) und Footer (klein)
 * verwendet. Klick führt zu Google-Maps-Rezensionen (REVIEW_SUMMARY.placeUrl).
 */
interface Props {
  /** "compact" = Header/Footer, "large" = Startseite-Sektionsteaser. */
  size?: "compact" | "large";
  /** Optionaler className-Override für Layout-Containern. */
  className?: string;
}

const GoogleLogo = ({ className = "" }: { className?: string }) => (
  <svg className={className} width="16" height="16" viewBox="0 0 48 48" role="img" aria-label="Google" aria-hidden="false">
    <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z" />
    <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z" />
    <path fill="#FBBC05" d="M11.69 28.18c-.44-1.32-.69-2.73-.69-4.18s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z" />
    <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7C13.42 14.62 18.27 10.75 24 10.75z" />
  </svg>
);

const GoogleRatingBadge = ({ size = "compact", className = "" }: Props) => {
  const ratingStr = REVIEW_SUMMARY.rating.toFixed(1).replace(".", ",");
  const isLarge = size === "large";

  return (
    <a
      href={REVIEW_SUMMARY.placeUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Google-Rezensionen ansehen: ${ratingStr} von 5,0 Sternen bei ${REVIEW_SUMMARY.count} Bewertungen`}
      className={`inline-flex items-center gap-2 rounded-full border border-border bg-card/80 hover:border-primary/60 hover:bg-card transition-colors ${
        isLarge ? "px-4 py-2" : "px-3 py-1.5 text-xs"
      } ${className}`}
    >
      <GoogleLogo className={isLarge ? "w-5 h-5" : "w-4 h-4"} />
      <span className="flex items-center gap-0.5" role="img" aria-label={`${ratingStr} von 5 Sternen`}>
        {Array.from({ length: 5 }).map((_, i) => (
          <Star key={i} size={isLarge ? 14 : 12} className="fill-amber-400 text-amber-400" aria-hidden="true" />
        ))}
      </span>
      <span className={`tabular-nums font-semibold text-foreground ${isLarge ? "text-sm" : "text-xs"}`}>
        {ratingStr}
      </span>
      {isLarge && (
        <span className="text-muted-foreground text-sm">
          · {REVIEW_SUMMARY.count} Bewertungen
        </span>
      )}
    </a>
  );
};

export default GoogleRatingBadge;
